import { clone, find, findIndex, forEach, get, has, isNull, isUndefined, keys } from 'lodash-es'
import commonConfigurationBundle from '../config/'

/**
 * Configuration bundles
 * Sorted by ascending order of precedence
 */
const loadedConfigurationBundles = [
  { common: commonConfigurationBundle },
  { client: false }
]

/**
 * Gets the requested configuration item (by its key & namespace)
 * @param key
 * @param namespace
 * @returns {*}
 */
const getItem = (key, namespace) => {
  let configurationValue = null
  forEach(clone(loadedConfigurationBundles).reverse(), (configuration) => {
    const configBundleData = Object.values(configuration)[0]
    if (configurationValue !== null) {
      return configurationValue
    }
    const configurationValueInBundle = configurationResolution(configBundleData, key, namespace)
    if (isUndefined(configurationValueInBundle)) {
      return
    }
    if (isNull(configurationValueInBundle) && !isNull(configurationValue)) {
      return
    }
    configurationValue = configurationValueInBundle
  })
  return parseConfigurationValue(configurationValue, this)
}

/**
 * Parses configuration values
 * @param configurationValue
 * @returns {any}
 */
const parseConfigurationValue = (configurationValue) => {
  // Does config value contain additional instructions for configurator?
  if (has(configurationValue, 'config.type')) {
    if (!has(configurationValue, 'config.value')) {
      return null
    }
    /*
    * Case when config value instructs configurator to look for an initialized js var in window object
    * example use case when we'd want a pointer to window.advAppData (initialized from footer_js.php)
    * !!WARNING: ALWAYS CHECK THE SCOPE OF THE VARIABLE YOU WANT TO REFERENCE. VARIABLES IN WINDOW SHOULD ALWAYS WORK,
    * AS LONG AS THEY ARE LOADED BEFORE THIS MODULE
    */
    if (configurationValue['config.type'] === 'references.variable.window') {
      // Is the configuration value safe to execute?
      const validJsVariablePattern = /^[_$a-zA-Z\xA0-\uFFFF][._$a-zA-Z0-9\xA0-\uFFFF]*$/i
      if (validJsVariablePattern.test(configurationValue['config.value'])) {
        // eslint-disable-next-line no-new-func
        return new Function('"use strict"; return window.' + configurationValue['config.value'])()
      }
    }
    /*
    * Case when config value instructs configurator to look for another config value, possibly from different namespace
    * example use case when we'd want to reuse a specific configuration value from a different module
    * !!WARNING: USE WISELY AND WITH EXTRA CARE. AVOID DEEP REFERENCES AS IT CAN LEAD TO CYCLIC DEPENDENCY HELL
     */
    if (configurationValue['config.type'] === 'references.setting.config') {
      if (has(configurationValue['config.value'], 'config.key')) {
        const namespace = (has(configurationValue['config.value'], 'config.namespace'))
          ? configurationValue['config.value']['config.namespace'] : null
        return getItem(configurationValue['config.value']['config.key'], namespace)
      }
      return getItem(configurationValue['config.value'])
    }
    return null
  }
  return configurationValue
}

/**
 * Given a list of configurations, it will attempt to retrieve a value
 * from a given key [and optionally a given namespace]
 * @param configuration
 * @param keySetting
 * @param keyNamespace
 * @returns {null|*}
 */
const configurationResolution = (configuration, keySetting, keyNamespace = null) => {
  if (keyNamespace !== null) {
    if (has(configuration[keyNamespace], keySetting)) {
      return get(configuration[keyNamespace], keySetting)
    }
    return null
  }
  const foundConfigSettings = find(configuration, keySetting)
  if (typeof foundConfigSettings === 'undefined') {
    return null
  }
  return foundConfigSettings[keySetting]
}

const ConfigService = {
  /**
   * Gets the config item
   * @param key
   * @param namespace
   * @returns {any}
   */
  get: (key, namespace = null) => {
    return getItem(key, namespace)
  },
  replaceLoadedBundle: (bundleName, payload) => {
    const bundleIdx = findIndex(loadedConfigurationBundles, (bundle) => get(keys(bundle), 0) === bundleName)
    loadedConfigurationBundles[bundleIdx] = { [bundleName]: payload }
  },
  getLoadedBundles: () => {
    return loadedConfigurationBundles
  }
}

export default ConfigService
